import { useEffect, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import {
  adminGetEcosystems,
  adminUpdateRealmPackages,
  type AdminPackageAvailability,
} from '@frontend/api-clients/ecosystemsClient';
import PlatformServicesChecklist from '@frontend/components/projects/PlatformServicesChecklist';
import type { ProjectServiceId } from '@frontend/components/projects/projectServices';

interface RealmPackagesPanelProps {
  expanded: boolean;
  onToggle: () => void;
}

export default function RealmPackagesPanel({ expanded, onToggle }: RealmPackagesPanelProps) {
  const [availability, setAvailability] = useState<AdminPackageAvailability | null>(null);
  const [selected, setSelected] = useState<ProjectServiceId[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!expanded) return;
    const load = async () => {
      try {
        setError('');
        const token = authClient.getToken();
        if (!token) throw new Error('You must be signed in.');
        const response = await adminGetEcosystems(token);
        setAvailability(response.packageAvailability);
        setSelected(response.packageAvailability.realmServices as ProjectServiceId[]);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load realm packages');
      }
    };
    void load();
  }, [expanded]);

  const save = async () => {
    try {
      setBusy(true);
      setError('');
      setMessage('');
      const token = authClient.getToken();
      if (!token) throw new Error('You must be signed in.');
      const services = await adminUpdateRealmPackages(token, selected);
      setSelected(services as ProjectServiceId[]);
      setAvailability((current) => current ? { ...current, realmServices: services } : current);
      setMessage('Realm package availability updated.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update realm packages');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ecosystem-card admin-widget-card">
      <button type="button" className="admin-widget-header" onClick={onToggle}>
        <div className="admin-widget-header-main">
          <h3 className="ecosystem-title">Realm packages</h3>
        </div>
        <span className="admin-widget-toggle">{expanded ? '▾' : '▸'}</span>
      </button>
      {expanded ? (
        <div className="admin-widget-body">
          <p className="ecosystem-description">
            Choose which installed platform packages ecosystem admins may activate.
          </p>
          {error ? <div className="settings-message settings-error">{error}</div> : null}
          {message ? <div className="settings-message settings-success">{message}</div> : null}
          <PlatformServicesChecklist
            idPrefix="realm-package"
            legend="Package availability"
            packageCatalog={availability?.catalog}
            availableServices={availability?.installedServices || []}
            realmServices={selected}
            ecosystemServices={selected}
            selectedServices={selected}
            canAddRealmServices
            canAddEcosystemServices
            readOnly={busy}
            onChange={setSelected}
          />
          <div className="admin-create-user-actions">
            <button type="button" className="btn btn-primary" disabled={busy} onClick={save}>
              {busy ? 'Saving…' : 'Save realm packages'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
